/**
 * Cart discount component.
 *
 * Applies and removes cart discount codes via POST /cart/update.js, then hands
 * the returned section HTML to the existing cart drawer renderer through the
 * 'update:cart:drawer' event.
 *
 * Note: `discount` is a full replacement list, not additive - every request
 * must send every code that should remain on the cart.
 */

/* ===== Module scoped so the accordion survives the drawer re-render ===== */
let keepOpen = false;

class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleAccordionToggle = this.handleAccordionToggle.bind(this);
    this.activeFetch = null;
    this.isAnimating = false;
  }

  connectedCallback() {
    window.wetheme?.webcomponentRegistry?.register({key: 'component-cart-discount'});

    this.sectionId = this.dataset.sectionId || 'cart-drawer';
    this.details = this.querySelector('details');
    this.form = this.querySelector('[data-cart-discount-form]');
    this.input = this.querySelector('[data-cart-discount-input]');
    this.submitButton = this.querySelector('[data-cart-discount-submit]');
    this.errorElement = this.querySelector('[data-cart-discount-error]');
    this.errorText = this.querySelector('[data-cart-discount-error-text]');
    this.strings = this.readStrings();

    this.summary = this.querySelector('summary');
    this.content = this.querySelector('.cart-discount__content');

    /* ===== Re-open if the shopper had opened it before the last re-render.
            Set directly rather than animated - the panel was already open ===== */
    if (this.details && keepOpen) this.details.open = true;

    if (this.form) this.form.addEventListener('submit', this.handleSubmit);
    if (this.summary) this.summary.addEventListener('click', this.handleAccordionToggle);
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener('submit', this.handleSubmit);
    if (this.summary) this.summary.removeEventListener('click', this.handleAccordionToggle);
    this.removeEventListener('click', this.handleClick);
    if (this.activeFetch) this.activeFetch.abort();
    clearTimeout(this.animationFallback);
  }

  /* ===== <details> cannot animate on its own, so the toggle is driven manually ===== */
  handleAccordionToggle(e) {
    if (!this.details || !this.content) return;

    e.preventDefault();
    if (this.isAnimating) return;

    const willOpen = !this.details.open;
    keepOpen = willOpen;

    if (willOpen) {
      this.details.open = true;
      this.animateHeight(0, this.content.scrollHeight);
    } else {
      this.classList.add('cart-discount--closing');
      this.animateHeight(this.content.scrollHeight, 0, () => {
        this.details.open = false;
        this.classList.remove('cart-discount--closing');
      });
    }
  }

  animateHeight(from, to, onComplete) {
    const {content} = this;

    const finish = () => {
      content.removeEventListener('transitionend', onTransitionEnd);
      clearTimeout(this.animationFallback);
      content.style.height = '';
      this.isAnimating = false;
      if (onComplete) onComplete();
    };

    const onTransitionEnd = (event) => {
      if (event.target === content && event.propertyName === 'height') finish();
    };

    /* ===== Respect the shopper's motion preference ===== */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      content.style.height = '';
      if (onComplete) onComplete();
      return;
    }

    this.isAnimating = true;
    content.style.height = `${from}px`;
    content.addEventListener('transitionend', onTransitionEnd);

    /* ===== Guard against transitionend never firing ===== */
    clearTimeout(this.animationFallback);
    this.animationFallback = setTimeout(finish, 400);

    requestAnimationFrame(() => {
      content.style.height = `${to}px`;
    });
  }

  handleSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.input) return;

    const code = this.input.value.trim();
    if (!code) return;

    keepOpen = true;

    /* ===== Already on the cart - nothing to do ===== */
    const existing = this.existingDiscounts();
    if (existing.some((existingCode) => existingCode.toUpperCase() === code.toUpperCase())) {
      this.input.value = '';
      return;
    }

    this.updateDiscounts([...existing, code], code);
  }

  handleClick(e) {
    const removeButton = e.target.closest('[data-cart-discount-remove]');
    if (!removeButton || !this.contains(removeButton)) return;

    e.preventDefault();

    const pill = removeButton.closest('[data-discount-code]');
    const code = pill?.dataset.discountCode;
    if (!code) return;

    keepOpen = true;
    this.updateDiscounts(
      this.existingDiscounts().filter((existingCode) => existingCode !== code),
      null
    );
  }

  /**
   * @param {string[]} codes - every code that should remain on the cart
   * @param {string|null} appliedCode - the code just entered, if this is an apply
   */
  async updateDiscounts(codes, appliedCode) {
    this.hideError();
    this.setLoading(true);

    if (this.activeFetch) this.activeFetch.abort();
    const abortController = new AbortController();
    this.activeFetch = abortController;

    try {
      const response = await fetch(window.routes.cart_update_url, {
        ...this.fetchConfig(),
        body: JSON.stringify({
          discount: codes.join(','),
          sections: [this.sectionId]
        }),
        signal: abortController.signal
      });

      if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);

      const data = await response.json();
      const html = data.sections?.[this.sectionId];

      if (appliedCode) {
        const applied = appliedCode.toUpperCase();

        /* ===== Shopify rejected the code outright ===== */
        const rejected = (data.discount_codes || []).some(
          (discount) => String(discount.code).toUpperCase() === applied && discount.applicable === false
        );

        if (rejected) {
          this.input.value = '';
          this.showError(this.strings.code_error);
          return;
        }

        /* ===== Shipping discounts report as applicable but never render a pill,
                because they only resolve at checkout once an address exists ===== */
        if (html && !this.renderedDiscounts(html).some((code) => code.toUpperCase() === applied)) {
          this.input.value = '';
          this.showError(this.strings.shipping_error);
          return;
        }
      }

      if (html) {
        window.eventBus?.emit('update:cart:drawer', data);
      } else {
        /* ===== The section did not render - the discount is already applied
                server side, so fall back to the drawer's own fetch rather than
                leaving the shopper looking at a stale total ===== */
        window.eventBus?.emit('render:cart:drawer');
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Failed to update cart discounts:', error);
      /* ===== Keep the typed value so the shopper can retry ===== */
      this.showError(this.strings.generic_error);
    } finally {
      this.activeFetch = null;
      this.setLoading(false);
    }
  }

  /* ===== Codes currently rendered in this element ===== */
  existingDiscounts() {
    return this.discountCodesIn(this);
  }

  /* ===== Codes rendered in a freshly returned section ===== */
  renderedDiscounts(html) {
    return this.discountCodesIn(new DOMParser().parseFromString(html, 'text/html'));
  }

  discountCodesIn(root) {
    return Array.from(root.querySelectorAll('[data-discount-code]'))
      .map((element) => element.dataset.discountCode)
      .filter(Boolean);
  }

  setLoading(isLoading) {
    this.classList.toggle('cart-discount--loading', isLoading);
    if (this.submitButton) this.submitButton.disabled = isLoading;
    if (this.input) this.input.disabled = isLoading;
  }

  showError(message) {
    if (!this.errorElement || !this.errorText) return;
    this.errorText.textContent = message;
    this.errorElement.hidden = false;
  }

  hideError() {
    if (this.errorElement) this.errorElement.hidden = true;
  }

  readStrings() {
    const fallback = {
      code_error: 'Discount code cannot be applied to your cart',
      shipping_error: 'Shipping discounts are shown at checkout after adding an address',
      generic_error: 'Something went wrong. Please try again.'
    };

    const script = this.querySelector('[data-cart-discount-strings]');
    if (!script) return fallback;

    try {
      return {...fallback, ...JSON.parse(script.textContent)};
    } catch (error) {
      return fallback;
    }
  }

  fetchConfig(type = 'json') {
    /* ===== Matches the fetch configuration used by the other cart components ===== */
    return {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: `application/${type}`}
    };
  }
}

customElements.define('cart-discount', CartDiscount);
